import { BillingOrder, SubscriptionPlanId, IndustryType } from '../types';

export interface ShopierCheckoutPayload {
  siteId?: string;
  userId: string;
  tenantId?: string;
  planId: string;
  isRenewal?: boolean;
  targetSubscriptionId?: string;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  billingAddress?: string;
  idempotencyKey?: string;
  termsVersion?: string;
  distanceSalesVersion?: string;
  legalAccepted?: boolean;
  immediateExecutionRequested?: boolean;
}

export interface ShopierCheckoutResponse {
  success: boolean;
  configured: boolean;
  orderId?: string;
  checkoutUrl?: string;
  checkoutHtml?: string;
  planName?: string;
  amount?: number;
  currency?: string;
  error?: string;
}

export interface ShopierHealthResponse {
  status: 'ok' | 'unconfigured' | 'error';
  configured: boolean;
  provider: string;
  plansAvailable: number;
  apiReachable?: boolean;
  error?: string;
}

export interface OrderStatusResponse {
  success: boolean;
  order: BillingOrder | null;
  isPaid: boolean;
  error?: string;
}

export class ShopierService {
  /**
   * Check Shopier server-side integration health
   */
  public static async checkHealth(): Promise<ShopierHealthResponse> {
    try {
      const res = await fetch('/api/billing/shopier/health');
      if (!res.ok) {
        return {
          status: 'error',
          configured: false,
          provider: 'Shopier',
          plansAvailable: 2,
          error: `HTTP ${res.status}`,
        };
      }
      return await res.json();
    } catch (err: any) {
      return {
        status: 'error',
        configured: false,
        provider: 'Shopier',
        plansAvailable: 2,
        error: err?.message || 'Ağ hatası',
      };
    }
  }

  /**
   * Initiate Real Shopier Checkout session via backend
   */
  public static async createCheckout(payload: ShopierCheckoutPayload): Promise<ShopierCheckoutResponse> {
    try {
      const res = await fetch('/api/billing/checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json, text/html',
        },
        body: JSON.stringify(payload),
      });

      const contentType = res.headers.get('content-type') || '';

      if (!contentType.includes('application/json')) {
        const rawText = await res.text();
        const isHtml = rawText.trim().startsWith('<') || rawText.includes('<!doctype html') || rawText.includes('<html');

        // Check if this is the SPA index.html fallback
        const isSpaFallback = rawText.includes('id="root"') || rawText.includes('/src/main.tsx') || rawText.includes('<title>Adiosoft');
        if (isSpaFallback) {
          return {
            success: false,
            configured: false,
            error: 'Backend API servisine ulaşılamadı. Lütfen sunucu durumunu ve API yapılandırmasını kontrol ediniz.',
          };
        }

        // Real Shopier payment form, hosted checkout page, or gateway HTML
        const hasShopierFormOrAction = (rawText.includes('<form') && rawText.includes('action=')) || rawText.includes('shopier') || rawText.includes('shopier.com');
        if (res.status === 200 && isHtml && hasShopierFormOrAction) {
          return {
            success: true,
            configured: true,
            checkoutHtml: rawText,
          };
        }

        // Non-payment HTML page (error/warning)
        console.warn(`[Shopier Client] Received non-payment HTML response (HTTP ${res.status}):`, rawText.slice(0, 150));
        return {
          success: false,
          configured: false,
          error: isHtml
            ? `Ödeme sağlayıcısından geçerli bir ödeme formu alınamadı (HTTP ${res.status}).`
            : `Sunucudan geçersiz veri formatı alındı (HTTP ${res.status}).`,
        };
      }

      const data = await res.json();
      if (!res.ok && data && !data.error) {
        data.error = `İşlem gerçekleştirilemedi (HTTP ${res.status}).`;
      }
      return data;
    } catch (err: any) {
      return {
        success: false,
        configured: false,
        error: err?.message || 'Ödeme oturumu başlatılırken ağ hatası oluştu.',
      };
    }
  }

  /**
   * Safely execute Shopier payment redirect or form submission in the browser
   */
  public static executeCheckout(checkout: { checkoutUrl?: string; checkoutHtml?: string }): void {
    // Prioritize direct hosted checkout form POST submission if present
    if (checkout.checkoutHtml) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = checkout.checkoutHtml;
      const form = tempDiv.querySelector('form');
      if (form) {
        form.target = '_top';
        document.body.appendChild(form);
        form.submit();
        return;
      }

      // Check for script redirect or meta refresh
      const redirectMatch = checkout.checkoutHtml.match(/window\.location(?:\.href)?\s*=\s*['"]([^'"]+)['"]/);
      if (redirectMatch && redirectMatch[1]) {
        try {
          if (window.top && window.top !== window) {
            window.top.location.href = redirectMatch[1];
            return;
          }
        } catch {
          // cross-origin fallback
        }
        window.location.assign(redirectMatch[1]);
        return;
      }

      document.open();
      document.write(checkout.checkoutHtml);
      document.close();
      return;
    }

    if (checkout.checkoutUrl) {
      try {
        if (window.top && window.top !== window) {
          window.top.location.href = checkout.checkoutUrl;
          return;
        }
      } catch {
        // cross-origin iframe security fallback
      }
      window.location.assign(checkout.checkoutUrl);
      return;
    }
  }



  /**
   * Query status of an order from backend (which verifies live with Shopier if needed)
   */
  public static async getOrderStatus(orderId: string): Promise<OrderStatusResponse> {
    try {
      const res = await fetch(`/api/billing/status/${encodeURIComponent(orderId)}`);
      const data = await res.json();
      return data;
    } catch (err: any) {
      return {
        success: false,
        order: null,
        isPaid: false,
        error: err?.message || 'Sipariş durumu sorgulanamadı.',
      };
    }
  }

  /**
   * Poll order status until paid or timeout
   */
  public static async pollOrderStatus(
    orderId: string,
    onStatusChange?: (status: OrderStatusResponse) => void,
    maxAttempts = 60,
    intervalMs = 2500
  ): Promise<boolean> {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const res = await this.getOrderStatus(orderId);
      if (onStatusChange) {
        onStatusChange(res);
      }
      if (res.isPaid) {
        return true;
      }
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
    return false;
  }

  /**
   * Fetch all orders for current user or admin
   */
  public static async getOrders(userId?: string): Promise<BillingOrder[]> {
    try {
      const url = userId ? `/api/billing/orders?userId=${encodeURIComponent(userId)}` : '/api/billing/orders';
      const res = await fetch(url);
      const data = await res.json();
      return data.orders || [];
    } catch {
      return [];
    }
  }

  /**
   * Check subscription health and return status
   */
  public static evaluateSubscriptionStatus(expireDate: number): {
    status: 'ACTIVE' | 'EXPIRING_SOON' | 'EXPIRED';
    daysRemaining: number;
    formattedExpireDate: string;
  } {
    const now = Date.now();
    const msRemaining = expireDate - now;
    const daysRemaining = Math.ceil(msRemaining / (1000 * 60 * 60 * 24));
    const formatted = new Date(expireDate).toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

    if (daysRemaining <= 0) {
      return { status: 'EXPIRED', daysRemaining: 0, formattedExpireDate: formatted };
    }
    if (daysRemaining <= 15) {
      return { status: 'EXPIRING_SOON', daysRemaining, formattedExpireDate: formatted };
    }
    return { status: 'ACTIVE', daysRemaining, formattedExpireDate: formatted };
  }
}

export default ShopierService;
